/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import mbpLogger from 'mbp-logger';
import { object, func } from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { joiResolver } from '@hookform/resolvers/joi';

// MUI
import { Button, Grid, CircularProgress, Dialog, DialogContent, DialogActions, Divider } from '@material-ui/core';

// Components
import AddProfile from './AddProfile/AddProfile';
import AddOccasions from './AddOccasions/AddOccasions';
import AddMailingAddress from './AddMailingAddress/AddMailingAddress';

// Icons
import leftarrowicon from '../Assets/leftarrowicon.svg';

// Helpers
import { formatNameToProperCapitalization, contryShort, unmaskPhoneNumber, getIsShowingLocationTypeField, maskPhoneNumber } from '../Helpers/Helpers';
import { getAccessTokenSafely } from '../../../../../../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';
import { getUserProfile } from '../../../../../../../../state/ducks/Checkout/ducks/Common/Common-Selectors';
import { schemaForAddNewRecipient } from '../Helpers/Schema';
import { RecipientBookContext } from '../Context/RecipientBookContext';
import { hasSuggestedAddress } from '../Helpers/QasHasAddress';

// Apis
import { trackEvent } from '../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { processAddNewRecipient } from '../../../../../../../../apis/account-apis/RecipientBook-apis/processAddNewRecipient';
import { getBrand } from '../../../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import PageTitle from '../../../../../common/PageTitle';
import { getAppRoutePrefix } from '../../../../../../../helpers/common/helper';

const AddNewRecipient = ({
    classes, recipientState, setRecipientState, setIsViewingAddNewRecipientForm,
}) => {
    const queryClient = useQueryClient();
    const {
        isVerificationNeeded, setIsVerificationNeeded, setConfirmAddress, formatedSelectedAddress, searchResults,
        setSearchResults, setFormatedSelectedAddress, keepAddressClicked, setIsLoadingConfirmation, setKeepAddressClicked, setChooseAddressClicked, setRecipientsCopy, recipientsCopy,
    } = useContext(RecipientBookContext);
    const [open, setOpen] = useState(true);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const relationshipTypes = queryClient.getQueryData(['relationships']).byValue;
    const userProfile = useSelector(getUserProfile) || {};
    const brandId = useSelector(getBrand).id;
    const { ContactId } = userProfile || '';
    const { addressBookResponse } = recipientsCopy || {};
    const { addressList } = addressBookResponse || [];
    const reminderPhone = maskPhoneNumber(recipientState?.addressBookResponse?.reminderPhone || '');

    const dispatch = useDispatch();

    const methods = useForm({
        mode: 'all',
        resolver: joiResolver(schemaForAddNewRecipient),
        defaultValues: {
            occasions: [{
                occasion: '2', desc: 'Birthday', dateMM: '', dateDD: '', dateYYYY: '', subscribedEmail: true, subscribedPhone: false, reminderPhone, reminderEmail: '',
            }],
        },
    });

    const processAddNewRecipientMutation = useMutation({
        mutationFn: processAddNewRecipient,
        onSuccess: (data) => {
            if (data && data.data !== undefined) {
                const {
                    addressNickName = '',
                    firstName = '',
                    lastName = '',
                    addressBookEntryId = '',
                    addressLineOne = '',
                    lastUpdateDate = '',
                    addressLineTwo = '',
                    employerName = '',
                    addressType = '',
                    city = '',
                    contactId = '',
                    country = '',
                    createdUser = '',
                    rcpFirstName = '',
                    isBillingAddress = false,
                    rcpLastName = '',
                    occasions = '',
                    rcpPhone = '',
                    relationship = '',
                    state = '',
                    zip = '',
                    rcpId = '',
                    defaultKey,
                    rcpEmployerName = '',
                } = data?.data?.addressResponse;
                const newContactFromResponse = {
                    contactId,
                    rcpFirstName,
                    rcpLastName,
                    relationship,
                    rcpEmployerName,
                    rcpId,
                    addresses: [], // add addresses as blank array if there is no address data added
                    occasions: [
                        ...occasions,
                    ],
                };
                // add addresses array if any of the address fields has been added, addressLineOne, city, state, zip, country are mendatory if any of them has been added by user for add new contact address form
                if (addressNickName || firstName || lastName || addressLineOne || addressLineTwo || city || state || zip || country || rcpPhone || addressType || employerName) {
                    newContactFromResponse.addresses = [
                        {
                            addressNickName,
                            firstName,
                            lastName,
                            addressBookEntryId,
                            addressLineOne,
                            addressLineTwo,
                            city,
                            state,
                            zip,
                            country,
                            rcpPhone,
                            isBillingAddress,
                            addressType,
                            relationship,
                            employerName,
                            lastUpdateDate,
                            rcpId,
                            defaultKey,
                            createdUser
                        },
                    ];
                };
                if (addressList) {
                    setRecipientState({ addressBookResponse: { ...addressBookResponse, addressList: [newContactFromResponse, ...addressList] } });
                    // RecipientsCopy is currently used as a copy of the recipientState
                    // inside of FilterRecipientBookModal to reset the filter to the unfiltered state
                    setRecipientsCopy({ addressBookResponse: { ...addressBookResponse, addressList: [newContactFromResponse, ...addressList] } });
                } else {
                    setRecipientState({ addressBookResponse: { ...addressBookResponse, addressList: [newContactFromResponse] } });
                    setRecipientsCopy({ addressBookResponse: { ...addressBookResponse, addressList: [newContactFromResponse] } });
                }
            }
            setOpen(false);
            setLoading(false);
            setIsLoadingConfirmation(false);
            setConfirmAddress(false);
            setIsViewingAddNewRecipientForm(false);
            window.history.pushState('', '', getAppRoutePrefix()+'/account/address-book');
            setError('');
            setTimeout(() => methods.reset(), 500);
        },
        onError: (err) => {
            setError('Something went wrong. Please try again.');
            setLoading(false);
            setIsLoadingConfirmation(false);
            setConfirmAddress(false);
            mbpLogger.logError({
                function: 'processAddNewRecipientMutation',
                appName: process.env.npm_package_name,
                module: 'AddNewRecipient.js',
                message: `Error While Adding a Recipient with Contact ID: ${userProfile?.ContactId}`,
                err,
            });
        },
    });

    const addContact = async (payload) => {
        setLoading(true);
        const jwtToken = await getAccessTokenSafely();
        processAddNewRecipientMutation.mutate({
            ContactId,
            data: payload,
            jwtToken,
        });
    };

    useEffect(() => {
        // clear QAS state on unmount
        return () => {
            setIsVerificationNeeded(false);
            setConfirmAddress(false);
            setSearchResults([])
            setFormatedSelectedAddress({});
            setKeepAddressClicked(false);
            setChooseAddressClicked(false);
        }
    }, []);

    useEffect(() => {
        if (keepAddressClicked && !isVerificationNeeded) {
            methods.handleSubmit(onSubmit)();
        }
    }, [keepAddressClicked, isVerificationNeeded]);

    const handleOnSubmit = (data) => {
        // delete data.occasions
        const [rcpFirstName, rcpLastName] = [formatNameToProperCapitalization(data.rcpFirstName), formatNameToProperCapitalization(data.rcpLastName)];
        const filteredOccasions = data.occasions.filter(occasion => (occasion.dateDD !== "" && occasion.dateMM !== "") || (Number(occasion.occasion) >= 5 && Number(occasion.occasion) <= 21));
        // remove if Antony says not needed
        const filteredOccasionsWithRcpId = filteredOccasions.map(occasion => ({
            ...occasion,
            reminderEmail: userProfile.email,
            reminderPhone: unmaskPhoneNumber(occasion?.reminderPhone || ''),
            subscribedEmail: (occasion.subscribedEmail === false && occasion.subscribed === true) ? occasion.subscribed : occasion.subscribedEmail,
            subscribedPhone: occasion.subscribedPhone || false,
            custFirstName: userProfile.firstName,
            custLastName: userProfile.lastName,
            createdUser: 'SSP',
            brandId
        }));
        const isBirthdayOccasionEntered = filteredOccasionsWithRcpId.findIndex(({ occasion }) => occasion === '2') > -1;
        // considering rcpFirstName as firstName and rcpLastName as lastName when user didnt added mailingfirstname or mailinglastname and as firstName and lastName is mandatory for add recipient endpoint
        const isAddressFieldsAdded = data.addressLineOne !== '' && data.city !== '' && data.state !== '' && data.zip !== '' && data.country !== '';
        const [firstName, lastName] = [(!isAddressFieldsAdded ? data.rcpFirstName : rcpFirstName), (!isAddressFieldsAdded ? data.rcpLastName : rcpLastName)];

        const unmaskedPhoneNumber = data.rcpPhone ? unmaskPhoneNumber(data.rcpPhone) : '';

        let payload = {
            ...data,
            occasions: filteredOccasionsWithRcpId,
            // technical debt - could we just format names properly inside react hook form?
            rcpFirstName,
            rcpLastName,
            relationship: relationshipTypes[data.relationship] || '',
            createdUser: 'SSP',
            defaultKey: '1', // in Add New Recipient, we always set the address as default because we can only add 1 address
            brandId,
            country: isAddressFieldsAdded ? contryShort[data.country] : '',
            rcpPhone: unmaskedPhoneNumber,
            employerName: data.employerName && getIsShowingLocationTypeField(data.addressType) ? data.employerName : '',
        };

        if (data.addressLineOne) {
            payload = {
                ...payload,
                firstName,
                lastName,
            }
        }

        const addedOccasions = filteredOccasionsWithRcpId.length > 0 ? filteredOccasionsWithRcpId.map((occasion) => occasion.desc).join('|') : 'No occasions added';
        dispatch(trackEvent({
            eventName: "address_book_contact_added",
            contact_added_method: "Form Submission",
            birthday_included: isBirthdayOccasionEntered,
            occasion: addedOccasions,
            eventCategory: "Address Book",
            eventAction: "Contact Added - Form",
            eventLabel: `Occasions: ${addedOccasions}||Birthday Included: ${isBirthdayOccasionEntered}`,
        }));
        addContact(payload);
    }

    const onSubmit = (data) => {
        if (isVerificationNeeded) {
            const hasAddress = hasSuggestedAddress({ formatedSelectedAddress, methods, searchResults });
            if (hasAddress) {
                setIsVerificationNeeded(false);
                return handleOnSubmit(data);
            } else {
                return setConfirmAddress(true);
            }
        }
        handleOnSubmit(data);
    };

    const hasErrors = !!Object.keys(methods.formState.errors).length > 0;
    return (
        <div>
            <Dialog fullScreen open={open} onClose={() => setOpen(false)}>
                <DialogContent sx={{ padding: '20px 24px 0px 20px' }}>
                    <div className={classes.backButtonContainer} onClick={() => { setOpen(false); setIsViewingAddNewRecipientForm(false); window.history.pushState('', '', getAppRoutePrefix()+'/account/address-book'); }} role="button" tabIndex={0} onKeyDown={() => { }}>
                        <span><img src={leftarrowicon} alt="left arrow icon" /></span>
                        <span className={classes.backButtonIcon}>Contacts</span>
                    </div>
                    <Grid container className={classes.addNewRecipientWrapper}>
                        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                        <Grid item xs={12} className={classes.pageTitleWrapper}>
                            <PageTitle title="Add New Contact" />
                        </Grid>
                        <FormProvider {...methods}>
                            <Grid container className="formStyle">
                                <form className={classes.formStyle}>
                                    <Grid item xs={12}>
                                        <AddProfile parentComponent='AddNewRecipient' />
                                    </Grid>
                                    <Divider className={classes.dividerStyle} />
                                    <Grid item xs={12} className={classes.marginTop24}>
                                        <AddOccasions classes={classes} parentComponent='AddNewRecipient' />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <AddMailingAddress classes={classes} />
                                    </Grid>
                                </form>
                            </Grid>
                        </FormProvider>
                    </Grid>
                    {error
                        && (
                            <Grid container justifyContent="center" alignItems="center">
                                <p className={classes.errorMessageContainer}>{error}</p>
                            </Grid>
                        )}
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Grid container justifyContent='center' spacing={2}>
                        <Grid item xs={6} lg={2}>
                            <Button
                                variant="outlined"
                                color="primary"
                                fullWidth
                                className={classes.outlinedPrimaryBtn}
                                style={{ height: '56px', borderRadius: '0px' }}
                                onClick={() => {
                                    setOpen(false);
                                    setIsViewingAddNewRecipientForm(false);
                                    window.history.pushState('', '', getAppRoutePrefix()+'/account/address-book');
                                    setError('');
                                    setTimeout(() => methods.reset(), 500);
                                }}
                            >
                                Cancel
                            </Button>
                            {
                                /* THIS COMPONENT IS SHARED BETWEEN BOTH MOBILE AND DESKTOP - SINCE EDS BUTTON ONLY WORKS FOR MOBILE FOR NOW, DISABLING THIS TO NOT BREAK DESKTOP */
                            }
                            {
                                /*
                                    This EDS button styles are being overriden by the button styles inside main.js

                                    We should figure out what is protecting other buttons from inheriting the styles inside main.js but this button
                                    is not protected. Disabling this button for now.
                                */
                            }
                            {/* <StyledButton
                                variant="secondary"
                                fullWidth
                                label='Cancel'
                                handleOnClick={() => {
                                    setOpen(false);
                                    setIsViewingAddNewRecipientForm(false);
                                    window.history.pushState('', '', '/account/address-book');
                                    setError('');
                                    setTimeout(() => methods.reset(), 500);
                                }}
                            /> */}
                        </Grid>
                        <Grid item xs={6} lg={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                className={classes.commonBtnContained}
                                style={{ height: '56px', borderRadius: '0px' }}
                                onClick={methods.handleSubmit(onSubmit)}
                                disabled={loading || hasErrors}
                            >
                                {loading ? <CircularProgress /> : 'Add Contact'}
                            </Button>
                            {
                                /* THIS COMPONENT IS SHARED BETWEEN BOTH MOBILE AND DESKTOP - SINCE EDS BUTTON ONLY WORKS FOR MOBILE FOR NOW, DISABLING THIS TO NOT BREAK DESKTOP */
                            }
                            {
                                /*
                                    This EDS button styles are being overriden by the button styles inside main.js

                                    We should figure out what is protecting other buttons from inheriting the styles inside main.js but this button
                                    is not protected. Disabling this button for now.
                                */
                            }
                            {/* <StyledButton
                                variant="primary"
                                fullWidth
                                label={loading ? <CircularProgress /> : 'Add Contact'}
                                handleOnClick={methods.handleSubmit(onSubmit, onError)}
                                disabled={loading}
                            /> */}
                        </Grid>
                    </Grid>
                </DialogActions>
            </Dialog>
        </div>
    );
};

AddNewRecipient.propTypes = {
    classes: object.isRequired,
    recipientState: object.isRequired,
    setRecipientState: func.isRequired,
    setIsViewingAddNewRecipientForm: func.isRequired,
};

export default AddNewRecipient;
